import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import CategoryFilter from '../../components/ui/CategoryFilter';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const SignIn = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showDemoCredentials, setShowDemoCredentials] = useState(true);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const { error } = await signIn(formData?.email, formData?.password);
      if (error) {
        setErrors({ general: error?.message || 'Invalid email or password' });
      } else {
        navigate('/e-book-catalog');
      }
    } catch (error) {
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setFormData({
      email: 'demo@strategyshelf.com',
      password: 'demo123',
    });
  };

  return (
    <>
      <Helmet>
        <title>Sign In - StrategyShelf</title>
        <meta name="description" content="Sign in to your StrategyShelf account to access your purchased eBooks and manage your library." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <CategoryFilter />

        <main className="pt-32 pb-12 md:pb-16 lg:pb-20">
          <div className="max-w-md mx-auto px-4 md:px-6">
            <div className="bg-card rounded-lg border border-border p-6 md:p-8">
              <div className="text-center mb-6">
                <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
                  Welcome Back
                </h1>
                <p className="text-sm text-muted-foreground">
                  Sign in to access your eBook library
                </p>
              </div>

              {showDemoCredentials && (
                <div className="bg-primary/10 rounded-md p-4 mb-6 relative">
                  <button
                    onClick={() => setShowDemoCredentials(false)}
                    className="absolute top-2 right-2 p-1 rounded-md hover:bg-primary/20 transition-smooth"
                    aria-label="Close demo credentials"
                  >
                    <Icon name="X" size={16} />
                  </button>
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-2">
                        Try Demo Account
                      </p>
                      <p className="text-xs text-muted-foreground mb-2">
                        Email: demo@strategyshelf.com
                        <br />
                        Password: demo123
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDemoLogin}
                        className="mt-2"
                      >
                        Use Demo Credentials
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {errors?.general && (
                <div className="bg-error/10 border border-error/20 rounded-md p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{errors?.general}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="your.email@example.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  required
                />

                <Input
                  type="password"
                  label="Password"
                  placeholder="Enter your password"
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  error={errors?.password}
                  required
                />

                <div className="flex items-center justify-between">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:underline focus-ring rounded-md"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  disabled={isLoading}
                  iconName={isLoading ? 'Loader2' : 'LogIn'}
                  iconPosition="left"
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link
                    to="/sign-up"
                    className="text-primary font-medium hover:underline focus-ring rounded-md"
                  >
                    Sign Up
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default SignIn;